import { supabase } from './client';
import { getCurrentUser } from './auth';
import { getEntry } from '../diary';

export const shareEntryWithEmail = async (entryId: string, email: string) => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // First check if the entry is already shared with this email
    const { data: existingShares, error: checkError } = await supabase
      .from('shared_diaries')
      .select('*')
      .eq('entry_id', entryId)
      .eq('email', email);
      
    if (checkError) throw checkError;
    
    if (existingShares && existingShares.length > 0) {
      return { 
        data: existingShares[0], 
        error: { message: 'This entry has already been shared with this email' } 
      };
    }
    
    const { data: accessCodeData, error: accessCodeError } = await supabase.rpc('generate_access_code');
    
    if (accessCodeError) throw accessCodeError;
    
    const accessCode = accessCodeData;
    
    const { data, error } = await supabase
      .from('shared_diaries')
      .insert({
        entry_id: entryId,
        owner_id: user.id,
        email,
        access_code: accessCode
      })
      .select()
      .single();
      
    if (error) throw error;
    
    // Get user profile for sender name
    const { data: profileData } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();
    
    const senderName = profileData?.full_name || user.email || 'A family member';
    
    // Get entry details for the email
    const entryData = await getEntry(entryId);
    const entryTitle = entryData?.babyName 
      ? `${entryData.babyName}'s diary entry` 
      : "Baby diary entry";
    
    console.log('Sending share notification with:', {
      entryId,
      recipientEmail: email,
      senderName,
      accessCode,
      entryTitle
    });
    
    // Send email notification
    try {
      const emailResponse = await supabase.functions.invoke('share-notification', {
        body: {
          entryId,
          recipientEmail: email,
          senderName,
          accessCode,
          entryTitle
        }
      });
      
      console.log('Email notification response:', emailResponse);
      
      if (emailResponse.error) {
        console.error('Error from edge function:', emailResponse.error);
        throw new Error(`Failed to send email: ${JSON.stringify(emailResponse.error)}`);
      }
      
      if (!emailResponse.data?.success) {
        console.error('Email not sent successfully:', emailResponse.data);
        throw new Error(`Email sending failed: ${JSON.stringify(emailResponse.data)}`);
      }
      
      console.log('Email notification sent successfully', emailResponse.data);
      return { data, error: null, emailSent: true };
    } catch (emailError) {
      console.error('Error sending email notification:', emailError);
      // Continue with the share process even if email fails
      // but store the error for user feedback
      return { 
        data, 
        error: emailError, 
        emailSent: false 
      };
    }
    
  } catch (error) {
    console.error('Error sharing entry:', error);
    return { data: null, error };
  }
};

export const getEntryShares = async (entryId: string) => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('shared_diaries')
      .select('*')
      .eq('entry_id', entryId)
      .eq('owner_id', user.id);
      
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error getting entry shares:', error);
    return { data: null, error };
  }
};

export const deleteEntryShare = async (shareId: string) => {
  try {
    const { error } = await supabase
      .from('shared_diaries')
      .delete()
      .eq('id', shareId);
      
    if (error) throw error;
    
    return { error: null };
  } catch (error) {
    console.error('Error deleting entry share:', error);
    return { error };
  }
};

export const getSharedEntry = async (accessCode: string, entryId: string) => {
  try {
    const { data: shareData, error: shareError } = await supabase
      .from('shared_diaries')
      .select('*')
      .eq('entry_id', entryId)
      .eq('access_code', accessCode)
      .single();
      
    if (shareError) throw shareError;
    
    return { data: shareData, error: null };
  } catch (error) {
    console.error('Error accessing shared entry:', error);
    return { data: null, error };
  }
};
